import { useEffect, useState } from 'react';
import style from './Weather.module.css';

/**
 * Live værvarsel for Fredrikstad. Backend (`/intranet/weather`) proxies MET
 * Norway's Locationforecast API and caches for 25 minutes.
 */

type Condition = 'sun' | 'cloud' | 'partly' | 'rain' | 'sleet' | 'snow';

interface WeatherData {
    location: string;
    temp: number | null;
    cond: Condition;
    text: string;
    wind: string;
    precip: string;
}

const FALLBACK: WeatherData = {
    location: 'Fredrikstad',
    temp: null,
    cond: 'cloud',
    text: 'Henter…',
    wind: '',
    precip: '',
};

function Icon({ c, size = 28 }: { c: Condition; size?: number }) {
    const s = { width: size, height: size };
    const stroke = '#1F140C';
    const sw = 1.4;
    switch (c) {
        case 'sun':
            return (
                <svg viewBox="0 0 32 32" style={s}>
                    <circle cx="16" cy="16" r="5" fill="none" stroke={stroke} strokeWidth={sw} />
                    {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
                        const r = (a * Math.PI) / 180;
                        const x1 = 16 + Math.cos(r) * 9;
                        const y1 = 16 + Math.sin(r) * 9;
                        const x2 = 16 + Math.cos(r) * 12.5;
                        const y2 = 16 + Math.sin(r) * 12.5;
                        return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />;
                    })}
                </svg>
            );
        case 'partly':
            return (
                <svg viewBox="0 0 32 32" style={s}>
                    <circle cx="11" cy="12" r="4.5" fill="none" stroke={stroke} strokeWidth={sw} />
                    {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
                        const r = (a * Math.PI) / 180;
                        const x1 = 11 + Math.cos(r) * 7;
                        const y1 = 12 + Math.sin(r) * 7;
                        const x2 = 11 + Math.cos(r) * 9.5;
                        const y2 = 12 + Math.sin(r) * 9.5;
                        return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth={1.1} strokeLinecap="round" />;
                    })}
                    <path d="M11 24c-2.5 0-4-1.5-4-3.5S8.5 17 11 17.2c0.4-1.7 2-3 4-3 2.5 0 4.4 1.8 4.6 4.1 0.2 0 0.4-0.1 0.6-0.1 1.6 0 3 1.3 3 3 0 1.7-1.4 2.8-3 2.8H11z"
                          fill="var(--bg-surface, #FBF5E7)" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
                </svg>
            );
        case 'cloud':
            return (
                <svg viewBox="0 0 32 32" style={s}>
                    <path d="M9 22c-2.5 0-4-1.5-4-3.5S6.5 15 9 15.2c0.4-1.7 2.2-3.2 4.5-3.2 3 0 5.4 2.2 5.6 5 0.2 0 0.4-0.1 0.6-0.1 1.6 0 3 1.3 3 3 0 1.7-1.4 2.1-3 2.1H9z"
                          fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
                </svg>
            );
        case 'rain':
            return (
                <svg viewBox="0 0 32 32" style={s}>
                    <path d="M9 17c-2.5 0-4-1.5-4-3.5S6.5 10 9 10.2c0.4-1.7 2.2-3.2 4.5-3.2 3 0 5.4 2.2 5.6 5 0.2 0 0.4-0.1 0.6-0.1 1.6 0 3 1.3 3 3 0 1.7-1.4 2.1-3 2.1H9z"
                          fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
                    <line x1="11" y1="22" x2="9" y2="27" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                    <line x1="16" y1="22" x2="14" y2="28" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                    <line x1="21" y1="22" x2="19" y2="27" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                </svg>
            );
        case 'sleet':
            return (
                <svg viewBox="0 0 32 32" style={s}>
                    <path d="M9 17c-2.5 0-4-1.5-4-3.5S6.5 10 9 10.2c0.4-1.7 2.2-3.2 4.5-3.2 3 0 5.4 2.2 5.6 5 0.2 0 0.4-0.1 0.6-0.1 1.6 0 3 1.3 3 3 0 1.7-1.4 2.1-3 2.1H9z"
                          fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
                    <line x1="11" y1="22" x2="9" y2="27" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                    <circle cx="16" cy="25" r="1" fill={stroke} />
                    <line x1="21" y1="22" x2="19" y2="27" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
                </svg>
            );
        case 'snow':
            return (
                <svg viewBox="0 0 32 32" style={s}>
                    <path d="M9 17c-2.5 0-4-1.5-4-3.5S6.5 10 9 10.2c0.4-1.7 2.2-3.2 4.5-3.2 3 0 5.4 2.2 5.6 5 0.2 0 0.4-0.1 0.6-0.1 1.6 0 3 1.3 3 3 0 1.7-1.4 2.1-3 2.1H9z"
                          fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
                    {[10, 16, 22].map((x, i) => (
                        <g key={i} transform={`translate(${x},25)`}>
                            <line x1="-2" y1="0" x2="2" y2="0" stroke={stroke} strokeWidth={sw} />
                            <line x1="0" y1="-2" x2="0" y2="2" stroke={stroke} strokeWidth={sw} />
                            <line x1="-1.4" y1="-1.4" x2="1.4" y2="1.4" stroke={stroke} strokeWidth={sw} />
                            <line x1="-1.4" y1="1.4" x2="1.4" y2="-1.4" stroke={stroke} strokeWidth={sw} />
                        </g>
                    ))}
                </svg>
            );
    }
}

export default function Weather() {
    const [data, setData] = useState<WeatherData>(FALLBACK);

    useEffect(() => {
        let cancelled = false;
        const fetchWeather = async () => {
            try {
                const resp = await fetch('/intranet/weather', { credentials: 'same-origin' });
                if (!resp.ok) return;
                const json = await resp.json();
                if (!cancelled) setData(json);
            } catch {
                /* keep fallback on error */
            }
        };
        fetchWeather();
        const id = setInterval(fetchWeather, 25 * 60 * 1000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    const meta = [data.wind, data.precip].filter(Boolean).join(' · ');

    return (
        <div className={style.weather}>
            <div className={style.row}>
                <div className={style.iconWrap}>
                    <Icon c={data.cond} size={36} />
                </div>
                <div className={style.temp}>
                    {data.temp ?? '—'}<span className={style.degree}>°</span>
                </div>
                <div className={style.text}>
                    <div className={style.condition}>{data.text}</div>
                    {meta && <div className={style.meta}>{meta}</div>}
                </div>
            </div>
        </div>
    );
}
